// ANSI 16/256/truecolor escape sequence → HTML spans.
// Handles SGR codes only (colors, bold, italic, underline, dim, reset).
// HTML-escapes the literal text content so dangerouslySetInnerHTML is safe.

const ESC = "\x1b[";

// Standard 16-color palette (dark-theme friendly, matches xterm defaults)
const ANSI16: string[] = [
    "#1e1e2e", // 0  black
    "#f38ba8", // 1  red
    "#a6e3a1", // 2  green
    "#f9e2af", // 3  yellow
    "#89b4fa", // 4  blue
    "#cba6f7", // 5  magenta
    "#94e2d5", // 6  cyan
    "#cdd6f4", // 7  white
    "#585b70", // 8  bright black
    "#f38ba8", // 9  bright red
    "#a6e3a1", // 10 bright green
    "#f9e2af", // 11 bright yellow
    "#89b4fa", // 12 bright blue
    "#cba6f7", // 13 bright magenta
    "#94e2d5", // 14 bright cyan
    "#cdd6f4", // 15 bright white
];

// Generate xterm 256-color palette
const PALETTE256: string[] = (() => {
    const p = [...ANSI16];
    // 216-color cube (6x6x6)
    const levels = [0, 95, 135, 175, 215, 255];
    for (let r = 0; r < 6; r++) for (let g = 0; g < 6; g++) for (let b = 0; b < 6; b++) {
        p.push(`rgb(${levels[r]},${levels[g]},${levels[b]})`);
    }
    // 24 grayscale ramp
    for (let i = 0; i < 24; i++) {
        const v = 8 + i * 10;
        p.push(`rgb(${v},${v},${v})`);
    }
    return p;
})();

function htmlEscape(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

interface Style {
    fg: string | null;
    bg: string | null;
    bold: boolean;
    dim: boolean;
    italic: boolean;
    underline: boolean;
}

function defaultStyle(): Style {
    return { fg: null, bg: null, bold: false, dim: false, italic: false, underline: false };
}

function styleToAttr(s: Style): string {
    const parts: string[] = [];
    if (s.fg) parts.push(`color:${s.fg}`);
    if (s.bg) parts.push(`background:${s.bg}`);
    if (s.bold) parts.push("font-weight:bold");
    if (s.dim) parts.push("opacity:0.5");
    if (s.italic) parts.push("font-style:italic");
    if (s.underline) parts.push("text-decoration:underline");
    return parts.join(";");
}

function applyCode(s: Style, codes: number[], i: number): number {
    const c = codes[i];
    if (c === 0) { Object.assign(s, defaultStyle()); return 1; }
    if (c === 1) { s.bold = true; return 1; }
    if (c === 2) { s.dim = true; return 1; }
    if (c === 3) { s.italic = true; return 1; }
    if (c === 4) { s.underline = true; return 1; }
    if (c === 22) { s.bold = false; s.dim = false; return 1; }
    if (c === 23) { s.italic = false; return 1; }
    if (c === 24) { s.underline = false; return 1; }
    if (c === 39) { s.fg = null; return 1; }
    if (c === 49) { s.bg = null; return 1; }
    if (c >= 30 && c <= 37) { s.fg = ANSI16[c - 30]; return 1; }
    if (c >= 40 && c <= 47) { s.bg = ANSI16[c - 40]; return 1; }
    if (c >= 90 && c <= 97) { s.fg = ANSI16[c - 90 + 8]; return 1; }
    if (c >= 100 && c <= 107) { s.bg = ANSI16[c - 100 + 8]; return 1; }
    if ((c === 38 || c === 48) && codes[i + 1] === 5 && i + 2 < codes.length) {
        const color = PALETTE256[codes[i + 2]] ?? null;
        if (c === 38) s.fg = color; else s.bg = color;
        return 3;
    }
    if ((c === 38 || c === 48) && codes[i + 1] === 2 && i + 4 < codes.length) {
        const color = `rgb(${codes[i + 2]},${codes[i + 3]},${codes[i + 4]})`;
        if (c === 38) s.fg = color; else s.bg = color;
        return 5;
    }
    return 1;
}

export function ansiToHtml(line: string): string {
    let html = "";
    let style = defaultStyle();
    let openSpan = false;
    let pos = 0;

    const flush = (text: string) => {
        if (!text) return;
        const attr = styleToAttr(style);
        if (attr) {
            if (openSpan) html += "</span>";
            html += `<span style="${attr}">`;
            openSpan = true;
        } else if (openSpan) {
            html += "</span>";
            openSpan = false;
        }
        html += htmlEscape(text);
    };

    while (pos < line.length) {
        const esc = line.indexOf(ESC, pos);
        if (esc === -1) { flush(line.slice(pos)); break; }
        flush(line.slice(pos, esc));

        // Find the end of the CSI sequence (letter [A-Za-z] after the [)
        let end = esc + 2;
        while (end < line.length && !/[A-Za-z]/.test(line[end])) end++;
        const letter = line[end];
        if (letter !== "m") { pos = end + 1; continue; } // skip non-SGR sequences

        const raw = line.slice(esc + 2, end);
        const codes = raw ? raw.split(";").map(Number) : [0];
        let i = 0;
        while (i < codes.length) i += applyCode(style, codes, i);
        pos = end + 1;
    }

    if (openSpan) html += "</span>";
    return html;
}
